import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** ビューポート外に追加でレンダリング（画像先読み）する行数 */
const OVERSCAN_ROWS = 10

type Props = {
  componentTemplate: (index: number) => React.ReactNode
  length: number
  colSize?: number
  /** スクロール位置変更時のコールバック（scrollTop px） */
  onScroll?: (scrollTop: number) => void
  /** スクロールコンテナの参照を親に渡す */
  scrollRef?: (el: HTMLDivElement | null) => void
  /** 初期スクロール位置（px） */
  initialScrollTop?: number
  /** アスペクト比 */
  aspectRatio?: 'landscape' | 'portrait'
}

export const GridLayout = ({
  componentTemplate,
  length,
  colSize = 3,
  onScroll,
  scrollRef,
  initialScrollTop,
  aspectRatio = 'portrait',
}: Props) => {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const initialScrollApplied = useRef(false)
  const initialScrollTopRef = useRef(initialScrollTop)
  useEffect(() => {
    initialScrollTopRef.current = initialScrollTop
  }, [initialScrollTop])

  // 行高さをCSSのcontain-intrinsic-sizeに使う
  const [rowHeight, setRowHeight] = useState(() => {
    const colWidth = window.innerWidth / colSize
    return Math.round(aspectRatio === 'portrait' ? colWidth * 1.5 : colWidth * (9 / 16))
  })

  const totalRows = Math.ceil(length / colSize)

  // ビューポート付近の行範囲（この範囲内はcontent-visibility不使用＝画像が即ロード）
  const [visibleRange, setVisibleRange] = useState<{ start: number; end: number }>(() => {
    const viewportHeight = window.innerHeight
    const colWidth = window.innerWidth / colSize
    const h = Math.round(aspectRatio === 'portrait' ? colWidth * 1.5 : colWidth * (9 / 16))
    const initScroll = initialScrollTop ?? 0
    const start = Math.max(0, Math.floor(initScroll / Math.max(h, 1)) - OVERSCAN_ROWS)
    const end = Math.min(totalRows, Math.ceil((initScroll + viewportHeight) / Math.max(h, 1)) + OVERSCAN_ROWS)
    return { start, end }
  })

  useEffect(() => {
    const update = () => {
      const colWidth = window.innerWidth / colSize
      setRowHeight(Math.round(aspectRatio === 'portrait' ? colWidth * 1.5 : colWidth * (9 / 16)))
    }
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [colSize, aspectRatio])

  const setParentRef = useCallback(
    (el: HTMLDivElement | null) => {
      parentRef.current = el
      scrollRef?.(el)
      const scrollTop = initialScrollTopRef.current
      if (el && !initialScrollApplied.current && scrollTop && scrollTop > 0) {
        initialScrollApplied.current = true
        el.scrollTop = scrollTop
      }
    },
    [scrollRef],
  )

  // スクロール時にビューポート付近の行範囲を更新
  const handleScroll = useCallback(() => {
    if (!parentRef.current) return
    const st = parentRef.current.scrollTop
    onScroll?.(st)

    if (rowHeight > 0) {
      const viewportHeight = parentRef.current.clientHeight
      const start = Math.max(0, Math.floor(st / rowHeight) - OVERSCAN_ROWS)
      const end = Math.min(totalRows, Math.ceil((st + viewportHeight) / rowHeight) + OVERSCAN_ROWS)
      setVisibleRange((prev) => {
        if (prev.start === start && prev.end === end) return prev
        return { start, end }
      })
    }
  }, [onScroll, rowHeight, totalRows])

  const widthStyle = useMemo(() => {
    if (colSize === 1) return 'w-screen'
    if (colSize === 2) return 'w-1/2'
    if (colSize === 3) return 'w-1/3'
    if (colSize === 4) return 'w-1/4'
    if (colSize === 5) return 'w-1/5'
    return 'w-1/3'
  }, [colSize])

  // 画面外の行用スタイル（content-visibility で描画をスキップ）
  const offscreenRowStyle = useMemo(
    () => ({
      contentVisibility: 'auto' as const,
      containIntrinsicBlockSize: `auto ${rowHeight}px`,
    }),
    [rowHeight],
  )

  // 行データをメモ化
  const rows = useMemo(() => {
    return Array.from({ length: totalRows }, (_, rowIndex) => ({
      rowIndex,
      cells: Array.from({ length: colSize }, (_, cellIndex) => rowIndex * colSize + cellIndex),
    }))
  }, [totalRows, colSize])

  return (
    <div ref={setParentRef} className="h-full overflow-y-auto" onScroll={handleScroll}>
      {rows.map(({ rowIndex, cells }) => (
        <div
          key={rowIndex}
          className="flex"
          style={rowIndex >= visibleRange.start && rowIndex < visibleRange.end ? undefined : offscreenRowStyle}
        >
          {cells.map((index, cellIndex) => (
            <div key={cellIndex} className={widthStyle}>
              {index < length && componentTemplate(index)}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
